import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { ContentCopyRounded, InfoOutlined } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { errorText } from '../../../lib/error-text';
import type { CopyModulesMode } from './template-modules.model';

// ─── Public API ─────────────────────────────────────────────────────────────
//
// Section keys mirror the CourseTemplate aggregate fields that the editor
// exposes. Each section copies INDEPENDENTLY — modules and assignments are
// the two "deep" sections that completely overwrite the local state.

export type CopyableSectionKey =
  | 'description'
  | 'objectives.course'
  | 'philosophy'
  | 'grading'
  | 'readings'
  | 'sessionFormat'
  | 'modules'
  | 'assignments'
  | 'latePolicy'
  | 'anchorVerse';

export interface CopySectionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Which section of the current template we're filling. */
  section: CopyableSectionKey;
  /** Friendly label for the section, shown in the dialog title. */
  sectionLabel: string;
  /** Tenant id used to scope the source-template picker. */
  tenantId: string;
  /** Current template id — excluded from the picker so users can't self-copy. */
  currentTemplateId: string;
  /**
   * Called with the extracted source value when the user confirms.
   * The editor is responsible for merging this into local state.
   * For `modules`, `modulesMode` says whether to copy everything or only the
   * structure (S2: no key texts from another course).
   */
  onCopy: (value: unknown, options: { modulesMode: CopyModulesMode }) => void;
}

interface TemplateSummary {
  id: string;
  title: string;
  code?: string;
  version: number;
  updatedAt?: Date;
  hasSection: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Return the section value from a raw Firestore template document. */
function extractSection(data: Record<string, unknown>, key: CopyableSectionKey): unknown {
  switch (key) {
    case 'objectives.course':
      return (data['objectives'] as { course?: unknown })?.course ?? [];
    default:
      return data[key];
  }
}

function hasMeaningfulValue(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    // TipTap doc → check for any content
    if ('type' in obj && obj['type'] === 'doc') {
      const content = obj['content'] as { content?: unknown[] }[] | undefined;
      return Array.isArray(content) && content.some((n) => (n.content?.length ?? 0) > 0);
    }
    return Object.keys(obj).length > 0;
  }
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
}

function summarize(section: CopyableSectionKey, v: unknown, t: TFunction<'sis'>): string {
  const k = 'templateEditor.copy.summary';
  if (!hasMeaningfulValue(v)) return t(`${k}.empty`);
  const count = Array.isArray(v) ? v.length : 0;
  switch (section) {
    case 'modules': {
      const sessions = (v as { sessions?: { scriptureRefs?: unknown[] }[] }[]).flatMap(
        (m) => m.sessions ?? [],
      );
      const withRefs = sessions.filter((x) => (x.scriptureRefs?.length ?? 0) > 0).length;
      return [
        t(`${k}.modules`, { count }),
        t(`${k}.sessions`, { count: sessions.length }),
        t(`${k}.withRefs`, { count: withRefs }),
      ].join(' · ');
    }
    case 'assignments':
      return t(`${k}.assignments`, { count });
    case 'philosophy':
      return t(`${k}.philosophy`, { count });
    case 'objectives.course':
      return t(`${k}.objectives`, { count });
    case 'grading': {
      const g = v as { components?: unknown[] };
      return t(`${k}.grading`, { count: g.components?.length ?? 0 });
    }
    case 'readings': {
      const r = v as { required?: unknown[]; complementary?: { items?: unknown[] } };
      return t(`${k}.readings`, {
        required: r.required?.length ?? 0,
        complementary: r.complementary?.items?.length ?? 0,
      });
    }
    case 'sessionFormat': {
      const sf = v as { weekdayTemplates?: unknown[] };
      return t(`${k}.sessionFormat`, { count: sf.weekdayTemplates?.length ?? 0 });
    }
    case 'description':
      return t(`${k}.description`);
    case 'latePolicy':
      return t(`${k}.latePolicy`);
    case 'anchorVerse': {
      const av = v as { reference?: string };
      return av.reference ?? t(`${k}.noReference`);
    }
    default:
      return t(`${k}.present`);
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export function CopySectionDialog({
  open,
  onClose,
  section,
  sectionLabel,
  tenantId,
  currentTemplateId,
  onCopy,
}: CopySectionDialogProps): JSX.Element {
  const { t } = useTranslation('sis');
  const [modulesMode, setModulesMode] = useState<CopyModulesMode>('structure');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [previewValue, setPreviewValue] = useState<unknown>(undefined);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ─── Load template list when dialog opens ──
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedId('');
    setPreviewValue(undefined);
    setModulesMode('structure');
    (async () => {
      try {
        const snap = await getDocs(collection(db, `tenants/${tenantId}/courseTemplates`));
        const rows: TemplateSummary[] = snap.docs
          .filter((d) => d.id !== currentTemplateId)
          .map((d) => {
            const data = d.data();
            if (data['deletedAt']) return null;
            const sec = extractSection(data as Record<string, unknown>, section);
            return {
              id: d.id,
              title: (data['title'] as string) ?? d.id,
              ...(data['code'] ? { code: data['code'] as string } : {}),
              version: (data['version'] as number) ?? 1,
              ...(data['updatedAt']
                ? { updatedAt: (data['updatedAt'] as { toDate(): Date }).toDate() }
                : {}),
              hasSection: hasMeaningfulValue(sec),
            } satisfies TemplateSummary;
          })
          .filter((t): t is TemplateSummary => t !== null)
          .sort((a, b) => {
            if (a.hasSection !== b.hasSection) return a.hasSection ? -1 : 1;
            return a.title.localeCompare(b.title);
          });
        if (!cancelled) {
          setTemplates(rows);
        }
      } catch (e) {
        if (!cancelled) {
          setError(errorText(e, t('templateEditor.copy.loadError')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, currentTemplateId, section, t]);

  // ─── Load preview when user picks a source ──
  useEffect(() => {
    if (!selectedId) {
      setPreviewValue(undefined);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, `tenants/${tenantId}/courseTemplates/${selectedId}`));
        if (cancelled) return;
        if (!snap.exists()) {
          setError(t('templateEditor.copy.gone'));
          setPreviewValue(undefined);
          return;
        }
        const value = extractSection(snap.data() as Record<string, unknown>, section);
        setPreviewValue(value);
      } catch (e) {
        if (!cancelled) {
          setError(errorText(e, t('templateEditor.copy.previewError')));
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, tenantId, section, t]);

  const handleConfirm = () => {
    if (!hasMeaningfulValue(previewValue)) {
      setError(t('templateEditor.copy.emptySection'));
      return;
    }
    onCopy(previewValue, { modulesMode });
    onClose();
  };

  const candidates = useMemo(() => templates.filter((t) => t.hasSection), [templates]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('templateEditor.copy.title', { section: sectionLabel })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" icon={<InfoOutlined fontSize="small" />}>
            {t('templateEditor.copy.replaceNotice')}
          </Alert>

          {loading ? (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={24} />
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel id="copy-source-label">{t('templateEditor.copy.source')}</InputLabel>
                <Select
                  labelId="copy-source-label"
                  label={t('templateEditor.copy.source')}
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value as string)}
                >
                  {candidates.length === 0 && (
                    <MenuItem value="" disabled>
                      {t('templateEditor.copy.noCandidates')}
                    </MenuItem>
                  )}
                  {candidates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {t.code && (
                          <Chip
                            label={t.code}
                            size="small"
                            sx={{ fontFamily: 'monospace', fontSize: 11, height: 20 }}
                          />
                        )}
                        <span>{t.title}</span>
                        <Chip
                          label={`v${t.version}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                {templates.length > 0 && candidates.length < templates.length && (
                  <Typography variant="caption" color="text.secondary" mt={0.5}>
                    {t('templateEditor.copy.hidden', {
                      count: templates.length - candidates.length,
                    })}
                  </Typography>
                )}
              </FormControl>

              {selectedId && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('templateEditor.copy.previewTitle')}
                  </Typography>
                  <Box mt={1}>
                    {previewLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Typography variant="body2" fontWeight={600}>
                        {summarize(section, previewValue, t)}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )}

              {section === 'modules' && (
                <FormControl>
                  <Typography variant="caption" fontWeight={700} id="copy-modules-mode">
                    {t('templateEditor.copy.modulesMode')}
                  </Typography>
                  <RadioGroup
                    aria-labelledby="copy-modules-mode"
                    value={modulesMode}
                    onChange={(e) => setModulesMode(e.target.value as CopyModulesMode)}
                  >
                    <FormControlLabel
                      value="structure"
                      control={<Radio size="small" />}
                      label={t('templateEditor.copy.modulesStructure')}
                    />
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label={t('templateEditor.copy.modulesAll')}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('templateEditor.copy.cancel')}</Button>
        <Button
          variant="contained"
          startIcon={<ContentCopyRounded />}
          onClick={handleConfirm}
          disabled={!selectedId || previewLoading || !hasMeaningfulValue(previewValue)}
        >
          {t('templateEditor.copy.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
